import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { projectAPI } from '../App';
import ProjectCalendar from './ProjectCalendar';
import ToDoList from './ToDoList';
import ProjectCard from './ProjectCard';
import { getPalette } from '../themePalette';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useTheme } from '../ThemeContext';
import {
  Home, UserPlus, ClipboardList, Mail, ListTodo, Calculator, Users, Package,
  Download, Sun, Moon, ChevronRight, ChevronDown, Plus, Bot, Search, CornerDownLeft,
} from 'lucide-react';

// API URL - uses runtime config (config.js) first, then build-time env, then origin
const PRODUCTION_API = (window.ENV?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin) + '/api';
const API_URL = PRODUCTION_API;
const LOGO = 'https://customer-assets.emergentagent.com/job_sleek-showcase-46/artifacts/c5c84fh5_Established%20logo.png';
const BACKEND_ROOT = API_URL.replace(/\/api$/, '');
const CMD_SECTIONS = [
  { label: 'All Projects', path: '/projects' },
  { label: 'AI Studio', path: '/ai-studio' },
  { label: 'Master To-Do', path: '/master-todo' },
  { label: 'Calculators', path: '/calculators' },
  { label: 'Contacts', path: '/master-contacts' },
  { label: 'Materials', path: '/master-materials' },
  { label: 'AI Assistant', path: '/ai-assistant' },
];

// Project accent colors for card headers - each project gets a unique color
const PROJECT_COLORS = [
  { bg: 'linear-gradient(135deg, #8B4513 0%, #A0522D 100%)', border: '#CD853F', name: 'Saddle Brown', accent: '#DEB887' },
  { bg: 'linear-gradient(135deg, #2F4F4F 0%, #3D5C5C 100%)', border: '#5F9EA0', name: 'Dark Slate', accent: '#87CEEB' },
  { bg: 'linear-gradient(135deg, #4A3728 0%, #5D4037 100%)', border: '#8D6E63', name: 'Coffee', accent: '#D7CCC8' },
  { bg: 'linear-gradient(135deg, #1C3A4B 0%, #2C5364 100%)', border: '#4682B4', name: 'Steel Blue', accent: '#87CEEB' },
  { bg: 'linear-gradient(135deg, #3E2723 0%, #4E342E 100%)', border: '#795548', name: 'Espresso', accent: '#BCAAA4' },
  { bg: 'linear-gradient(135deg, #1A237E 0%, #283593 100%)', border: '#5C6BC0', name: 'Indigo', accent: '#9FA8DA' },
  { bg: 'linear-gradient(135deg, #004D40 0%, #00695C 100%)', border: '#26A69A', name: 'Teal', accent: '#80CBC4' },
  { bg: 'linear-gradient(135deg, #BF360C 0%, #E64A19 100%)', border: '#FF7043', name: 'Deep Orange', accent: '#FFAB91' },
];

// Rotating interior cover photos for the project cards (approved in the preview)
const COVERS = [
  'https://images.unsplash.com/photo-1648881806148-e5c51179c826?crop=entropy&cs=srgb&fm=jpg&w=600&q=70&ixlib=rb-4.1.0',
  'https://images.unsplash.com/photo-1564078516393-cf04bd966897?crop=entropy&cs=srgb&fm=jpg&w=600&q=70&ixlib=rb-4.1.0',
  'https://images.unsplash.com/photo-1611048267451-e6ed903d4a38?crop=entropy&cs=srgb&fm=jpg&w=600&q=70&ixlib=rb-4.1.0',
  'https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?crop=entropy&cs=srgb&fm=jpg&w=600&q=70&ixlib=rb-4.1.0',
  'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?crop=entropy&cs=srgb&fm=jpg&w=600&q=70&ixlib=rb-4.1.0',
];

// Map an item status → a semantic tone key for the status overview
const STATUS_TONE = {
  'APPROVED': 'success', 'TO BE SELECTED': 'grey', 'ORDERED': 'warning',
  'IN PRODUCTION': 'brown', 'IN TRANSIT': 'purple', 'RECEIVED': 'teal',
  'SHIPPED': 'gold', 'BACKORDERED': 'danger', 'CONFIRMED': 'gold',
  'DELIVERED TO JOB SITE': 'teal', 'DELIVERED': 'teal', 'INSTALLED': 'success',
  'READY FOR INSTALL': 'teal', 'INSTALLING': 'purple', 'OUT FOR DELIVERY': 'purple',
  '(no status)': 'grey',
};
const PRIO_TONE = { urgent: 'danger', high: 'warning', High: 'warning', medium: 'grey', Medium: 'grey', low: 'muted', Low: 'muted' };

const MainDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { theme, toggle } = useTheme();
  const dark = theme !== 'light';
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailData, setEmailData] = useState({ email: '', name: '' });
  const [sendingEmail, setSendingEmail] = useState(false);
  // New-Home Wizard: create a project and auto-build its trade sheets from templates.
  const [showWizard, setShowWizard] = useState(false);
  const [wizName, setWizName] = useState('');
  const [wizClient, setWizClient] = useState('');
  const [wizBusy, setWizBusy] = useState(false);
  // Houzz Pro PDF import: preview parsed items, then commit to FF&E or Checklist,
  // and match a "tear sheet" PDF's finish swatches onto items by SKU.
  const [houzzBusy, setHouzzBusy] = useState(null);
  const [houzzPreview, setHouzzPreview] = useState(null); // {project, target, file, data}
  const [houzzProgress, setHouzzProgress] = useState(null); // {pct, stage}
  const [finishUpgradeCover, setFinishUpgradeCover] = useState(false);
  // Bulk Finish Fix: list FF&E items still missing a finish swatch and let the
  // user attach one by hand, one place, right after a tear-sheet run.
  const [missingFinish, setMissingFinish] = useState(null); // {project, items}
  const [missingBusy, setMissingBusy] = useState(false);
  // Undo Last Import: the most recent Houzz import per project can be rolled back.
  const [lastImports, setLastImports] = useState({}); // {projectId: batchSummary | null}
  // New dashboard: real per-project progress + cross-project status overview.
  const [projectStats, setProjectStats] = useState({}); // {projectId: {total_items, completion_percentage}}
  const [statusSummary, setStatusSummary] = useState(null); // /analytics/summary
  const [expandedTools, setExpandedTools] = useState(null); // projectId whose import tools are open
  // Swatch / PDF reuse library: every uploaded FF&E + tear-sheet PDF is kept so it
  // can be re-run later without re-uploading.
  const [savedPdfs, setSavedPdfs] = useState({}); // {projectId: [records]}
  const [pdfLibrary, setPdfLibrary] = useState(null); // project whose library modal is open
  const [pdfBusy, setPdfBusy] = useState(false);
  const [rerunPreview, setRerunPreview] = useState(null); // {project, doc, data}
  const [rerunBusy, setRerunBusy] = useState(false);
  const [coverBusy, setCoverBusy] = useState(null); // projectId whose cover is uploading
  const [cmdOpen, setCmdOpen] = useState(false);
  const [cmdQuery, setCmdQuery] = useState('');
  const [cmdRemote, setCmdRemote] = useState({ items: [], rooms: [], vendors: [] });

  const T = getPalette(theme);
  const tone = (k) => T[k] || T.grey;

  const previewHouzzImport = async (project, target, e) => {
    const fileEl = e.target;
    const file = fileEl.files && fileEl.files[0];
    if (!file) return;
    setHouzzBusy(`${project.id}:${target}`);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('target', target);
      const res = await fetch(`${API_URL}/projects/${project.id}/import-houzz-pdf/preview`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Preview failed');
      setHouzzPreview({ project, target, file, data });
    } catch (err) {
      window.alert('Preview failed: ' + (err.message || err));
    } finally {
      setHouzzBusy(null);
      fileEl.value = '';
    }
  };

  const confirmHouzzImport = async () => {
    if (!houzzPreview) return;
    const { project, target, file } = houzzPreview;
    setHouzzBusy(`commit`);
    setHouzzProgress({ pct: 0, stage: 'Uploading your PDF…' });
    try {
      // Import as a BACKGROUND job and poll — a large schedule takes longer than
      // the gateway request timeout, so a synchronous call returned a false 502
      // even though the server finished. The job returns a job_id instantly.
      const fd = new FormData();
      fd.append('ffe_file', file);
      fd.append('target', target);
      const res = await fetch(`${API_URL}/projects/${project.id}/import-houzz-combined`, { method: 'POST', body: fd });
      let start;
      try { start = await res.json(); } catch { throw new Error('Upload did not go through — please try again.'); }
      if (!res.ok || !start.job_id) throw new Error(start.detail || 'Could not start the import.');

      const jobId = start.job_id;
      setHouzzProgress({ pct: 3, stage: 'Starting import…' });
      const deadline = Date.now() + 15 * 60 * 1000;
      let result = null;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 2500));
        let job;
        try {
          const jr = await fetch(`${API_URL}/projects/${project.id}/import-jobs/${jobId}`);
          job = await jr.json();
        } catch { continue; }
        if (job && job.progress) setHouzzProgress(job.progress);
        if (job.status === 'done') { result = job.result || {}; break; }
        if (job.status === 'error') { throw new Error(job.error || 'Import failed'); }
      }
      if (!result) throw new Error('The import is taking longer than expected. It may still be finishing — refresh in a minute to check.');

      const data = result.ffe || {};
      const tabName = target === 'checklist' ? 'Checklist' : 'FF&E';
      setHouzzPreview(null);
      refreshLastImport(project.id);
      const roomCount = (data.rooms_named && data.rooms_named.length) || data.rooms || 0;
      const extra = target === 'ffe' ? ' and mirrored to the Checklist' : ' (Checklist only — transfer to FF&E when ready)';
      window.alert(`Imported ${data.products} products into ${project.name} → ${tabName}${extra} — ${roomCount} rooms, ${data.photos} photos.`);
      navigate(`/project/${project.id}?tab=${encodeURIComponent(tabName)}`);
    } catch (err) {
      window.alert('Import failed: ' + (err.message || err));
    } finally {
      setHouzzBusy(null);
      setHouzzProgress(null);
    }
  };

  const handleFinishImport = async (project, e) => {
    const fileEl = e.target;
    const file = fileEl.files && fileEl.files[0];
    if (!file) return;
    setHouzzBusy(`${project.id}:finish`);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('upgrade_cover', finishUpgradeCover ? 'true' : 'false');
      const res = await fetch(`${API_URL}/projects/${project.id}/import-finish-photos`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Finish import failed');
      const cov = finishUpgradeCover ? ' Cover photos also upgraded to hi-res.' : '';
      window.alert(`Finish photos matched by SKU: ${data.matched_skus} products → ${data.items_updated} items updated with their finish/color swatch.${cov} ${data.unmatched_skus.length} tear-sheet SKUs had no match.`);
    } catch (err) {
      window.alert('Finish import failed: ' + (err.message || err));
    } finally {
      setHouzzBusy(null);
      fileEl.value = '';
    }
  };

  const openMissingFinish = async (project, e) => {
    if (e) e.stopPropagation();
    setMissingBusy(true);
    try {
      const res = await fetch(`${API_URL}/projects/${project.id}/missing-finish-items`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to load');
      setMissingFinish({ project, items: data.items || [] });
    } catch (err) {
      window.alert('Could not load missing-finish items: ' + (err.message || err));
    } finally {
      setMissingBusy(false);
    }
  };

  const uploadFinishForItem = async (itemId, e) => {
    const fileEl = e.target;
    const file = fileEl.files && fileEl.files[0];
    if (!file) return;
    setMissingBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('item_id', itemId);
      fd.append('image_type', 'finish');
      const res = await fetch(`${API_URL}/upload-item-image`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || data.message || 'Upload failed');
      setMissingFinish((prev) => (prev ? { ...prev, items: (prev.items || []).filter((x) => x.id !== itemId) } : prev));
    } catch (err) {
      window.alert('Upload failed: ' + (err.message || err));
    } finally {
      setMissingBusy(false);
      fileEl.value = '';
    }
  };

  const refreshLastImport = async (projectId) => {
    try {
      const res = await fetch(`${API_URL}/projects/${projectId}/last-import`);
      const data = await res.json();
      setLastImports((prev) => ({ ...prev, [projectId]: data.has_undo ? data.batch : null }));
    } catch (err) { /* non-critical */ }
  };

  const undoLastImport = async (project, e) => {
    if (e) e.stopPropagation();
    const info = lastImports[project.id];
    const msg = info
      ? `Undo the last import for ${project.name}?\n\nThis will delete ${info.created_items} item(s) it added and restore ${info.updated_items} item(s) it refreshed to their prior values.`
      : `Undo the last import for ${project.name}?`;
    if (!window.confirm(msg)) return;
    setHouzzBusy(`${project.id}:undo`);
    try {
      const res = await fetch(`${API_URL}/projects/${project.id}/undo-houzz-import`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Undo failed');
      window.alert(`Undone: ${data.deleted_items} item(s) removed, ${data.restored_items} restored, ${data.removed_rooms} empty room(s) cleaned up.`);
      refreshLastImport(project.id);
    } catch (err) {
      window.alert('Undo failed: ' + (err.message || err));
    } finally {
      setHouzzBusy(null);
    }
  };

  const autoMatchFinishes = async (e) => {
    const fileEl = e.target;
    const file = fileEl.files && fileEl.files[0];
    if (!file || !missingFinish) return;
    const project = missingFinish.project;
    setMissingBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('upgrade_cover', 'false');
      const res = await fetch(`${API_URL}/projects/${project.id}/import-finish-photos`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Auto-match failed');
      const mres = await fetch(`${API_URL}/projects/${project.id}/missing-finish-items`);
      const mdata = await mres.json();
      setMissingFinish((prev) => (prev ? { ...prev, items: mdata.items || [] } : prev));
      window.alert(`Auto-matched ${data.items_updated} item(s) from the tear sheet. ${(data.unmatched_skus || []).length} SKU(s) had no match — pick those by hand below.`);
    } catch (err) {
      window.alert('Auto-match failed: ' + (err.message || err));
    } finally {
      setMissingBusy(false);
      fileEl.value = '';
    }
  };

  const openVendorOrderSheet = (project, e) => {
    if (e) e.stopPropagation();
    window.open(`${API_URL}/projects/${project.id}/vendor-order-sheet/print`, '_blank', 'noopener');
  };

  const loadSavedPdfs = async (projectId) => {
    try {
      const res = await fetch(`${API_URL}/projects/${projectId}/imported-pdfs`);
      const data = await res.json();
      setSavedPdfs((prev) => ({ ...prev, [projectId]: data.pdfs || [] }));
      return data.pdfs || [];
    } catch (err) { return []; }
  };

  const openPdfLibrary = async (project, e) => {
    if (e) e.stopPropagation();
    await loadSavedPdfs(project.id);
    setPdfLibrary(project);
  };

  const rerunSavedPdf = async (project, doc) => {
    setRerunBusy(true);
    try {
      const res = await fetch(`${API_URL}/projects/${project.id}/imported-pdfs/${doc.id}/rerun-preview`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Preview failed');
      setRerunPreview({ project, doc, data });
    } catch (err) {
      window.alert('Preview failed: ' + (err.message || err));
    } finally {
      setRerunBusy(false);
    }
  };

  const confirmRerun = async () => {
    if (!rerunPreview) return;
    const { project, doc } = rerunPreview;
    setRerunBusy(true);
    try {
      const res = await fetch(`${API_URL}/projects/${project.id}/imported-pdfs/${doc.id}/rerun`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Re-run failed');
      const s = data.summary || {};
      if (data.kind === 'tear_sheet') {
        window.alert(`Re-matched from ${doc.filename}: ${s.matched_skus ?? 0} products → ${s.items_updated ?? 0} items updated with their finish/color swatch.`);
      } else {
        window.alert(`Re-imported ${doc.filename} → ${data.kind === 'checklist' ? 'Checklist' : 'FF&E'}: ${s.items ?? 0} new, ${s.updated ?? 0} refreshed, ${s.rooms ?? 0} rooms.`);
      }
      setRerunPreview(null);
      refreshLastImport(project.id);
      loadProjectStats(project.id);
    } catch (err) {
      window.alert('Re-run failed: ' + (err.message || err));
    } finally {
      setRerunBusy(false);
    }
  };

  const uploadCover = async (project, e) => {
    const fileEl = e.target;
    const file = fileEl.files && fileEl.files[0];
    if (!file) return;
    setCoverBusy(project.id);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/projects/${project.id}/cover`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || 'Cover upload failed');
      setProjects((prev) => prev.map((p) => (p.id === project.id ? { ...p, cover_image: data.cover_image } : p)));
    } catch (err) {
      window.alert('Cover upload failed: ' + (err.message || err));
    } finally {
      setCoverBusy(null);
      fileEl.value = '';
    }
  };

  // Set the cover to a photo already in the project (no upload) — chosen in CoverPicker.
  const setCoverUrl = async (project, url) => {
    setCoverBusy(project.id);
    try {
      const res = await fetch(`${API_URL}/projects/${project.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cover_image: url }),
      });
      if (!res.ok) throw new Error('Failed to set cover');
      setProjects((prev) => prev.map((p) => (p.id === project.id ? { ...p, cover_image: url } : p)));
    } catch (err) {
      window.alert('Failed to set cover: ' + (err.message || err));
    } finally {
      setCoverBusy(null);
    }
  };

  // Drag project cards on the dashboard into the order you want; persisted per card.
  const onProjectsDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination || destination.index === source.index) return;
    const arr = Array.from(projects);
    const [moved] = arr.splice(source.index, 1);
    arr.splice(destination.index, 0, moved);
    const reordered = arr.map((p, i) => ({ ...p, dashboard_order: i }));
    setProjects(reordered);
    reordered.forEach((p) => {
      fetch(`${API_URL}/projects/${p.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ dashboard_order: p.dashboard_order }) }).catch(() => {});
    });
  };

  const deleteSavedPdf = async (project, doc) => {
    if (!window.confirm(`Remove "${doc.filename}" from the saved library? (This does not change any items already imported.)`)) return;
    setPdfBusy(true);
    try {
      await fetch(`${API_URL}/projects/${project.id}/imported-pdfs/${doc.id}`, { method: 'DELETE' });
      await loadSavedPdfs(project.id);
    } catch (err) {
      window.alert('Delete failed: ' + (err.message || err));
    } finally {
      setPdfBusy(false);
    }
  };

  const runNewHomeWizard = async () => {
    if (!wizName.trim() || wizBusy) return;
    setWizBusy(true);
    try {
      const BACKEND_URL = window.ENV?.REACT_APP_BACKEND_URL || window.location.origin;
      const res = await fetch(`${BACKEND_URL}/api/projects`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: wizName.trim(), client_info: { full_name: wizClient.trim() || wizName.trim(), email: '', phone: '', address: '' } }),
      });
      if (!res.ok) throw new Error('create failed');
      const proj = await res.json();
      // Auto-build every trade sheet from each trade's default template.
      try {
        const ov = await (await fetch(`${BACKEND_URL}/api/projects/${proj.id}/sheet-templates-overview`)).json();
        const sel = {};
        (ov.sheets || []).forEach((s) => {
          const tpls = s.templates || [];
          if (tpls.length) { const def = tpls.find((t) => t.is_default) || tpls[0]; sel[s.sheet_key] = def.id; }
        });
        if (Object.keys(sel).length) {
          await fetch(`${BACKEND_URL}/api/projects/${proj.id}/build-all-sheets`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ selections: sel }),
          });
        }
      } catch { /* templates optional */ }
      setShowWizard(false); setWizName(''); setWizClient('');
      navigate(`/project/${proj.id}?tab=Trade Sheets`);
    } catch (e) {
      alert('Could not create the home: ' + e.message);
    } finally {
      setWizBusy(false);
    }
  };

  const doBackup = async () => {
    try {
      const BACKEND_URL = window.ENV?.REACT_APP_BACKEND_URL || window.location.origin;
      const response = await fetch(`${BACKEND_URL}/api/backup/full`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url;
        a.download = `backup_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a); a.click();
        window.URL.revokeObjectURL(url); a.remove();
        alert('✅ Backup downloaded!');
      }
    } catch (error) { alert('Failed: ' + error.message); }
  };

  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'add-item') {
      const data = {
        name: searchParams.get('name'),
        price: searchParams.get('price'),
        sku: searchParams.get('sku'),
        size: searchParams.get('size'),
        finish_color: searchParams.get('finish'),
        finish_image: searchParams.get('finish_image'),
        vendor: searchParams.get('vendor'),
        link: searchParams.get('link'),
        image_url: searchParams.get('image'),
        msrp: searchParams.get('msrp'),
        remarks: searchParams.get('remarks')
      };
      localStorage.setItem('extensionScrapedData', JSON.stringify(data));
      alert(`✅ Product scraped!\n\nName: ${data.name}\nPrice: $${data.price || 'N/A'}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [searchParams]);

  useEffect(() => {
    // Resilient project load: the summary endpoint can time out on a cold
    // container, which used to blank the whole dashboard to "0 projects".
    // Retry a few times, cache the last good list, and NEVER wipe an existing
    // list on a transient error — so files show up consistently, every time.
    const mapProjects = (projectsData) => {
      const mapped = projectsData.map((project, index) => ({
        id: project.id,
        name: project.name,
        clientName: project.client_info?.full_name || 'Unknown Client',
        address: project.client_info?.address || '',
        cover_image: project.cover_image || '',
        dashboard_order: project.dashboard_order ?? null,
        active: project.active !== false,
        status: project.status || 'In Progress',
        color: PROJECT_COLORS[index % PROJECT_COLORS.length]
      }));
      mapped.sort((a, b) => (a.dashboard_order ?? 1e9) - (b.dashboard_order ?? 1e9));
      return mapped;
    };

    const fetchProjects = async (attempt = 0) => {
      try {
        const response = await fetch(`${PRODUCTION_API}/projects/summary`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'Cache-Control': 'no-cache' }
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        const projectsData = await response.json();
        const mappedProjects = mapProjects(projectsData);
        setProjects(mappedProjects);
        try { localStorage.setItem('cached_dashboard_projects', JSON.stringify(mappedProjects)); } catch (e) {}
        setLoading(false);
        for (const project of mappedProjects) {
          refreshLastImport(project.id);
          loadProjectStats(project.id);
          loadSavedPdfs(project.id);
        }
      } catch (error) {
        console.error(`❌ PROJECTS FETCH ERROR (attempt ${attempt + 1}):`, error);
        if (attempt < 2) {
          await new Promise((r) => setTimeout(r, 700));
          return fetchProjects(attempt + 1);
        }
        // Give up gracefully: keep what we already have, or hydrate from cache —
        // but never blank an already-populated dashboard.
        setProjects((prev) => {
          if (prev && prev.length) return prev;
          try {
            const cached = localStorage.getItem('cached_dashboard_projects');
            if (cached) return JSON.parse(cached);
          } catch (e) {}
          return [];
        });
        setLoading(false);
      }
    };
    fetchProjects();
    loadStatusSummary();
  }, []);

  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setCmdOpen((v) => !v); }
      if (e.key === 'Escape') setCmdOpen(false);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  // ⌘K "Search Everything": debounced global search for FF&E items, rooms & vendors.
  useEffect(() => {
    const q = cmdQuery.trim();
    if (q.length < 2) { setCmdRemote({ items: [], rooms: [], vendors: [] }); return; }
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`${API_URL}/search?q=${encodeURIComponent(q)}`);
        const d = await res.json();
        setCmdRemote({ items: d.items || [], rooms: d.rooms || [], vendors: d.vendors || [] });
      } catch (e) { setCmdRemote({ items: [], rooms: [], vendors: [] }); }
    }, 200);
    return () => clearTimeout(t);
  }, [cmdQuery]);

  // Change a project's status straight from the dashboard card — no need to
  // open the project. Reflected everywhere (dashboard + View all read status).
  const changeProjectStatus = async (project, newStatus) => {
    setProjects((prev) => prev.map((p) => (p.id === project.id ? { ...p, status: newStatus } : p)));
    try {
      await fetch(`${API_URL}/projects/${project.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
    } catch (err) {
      window.alert('Failed to update status: ' + (err.message || err));
    }
  };

  const loadProjectStats = async (projectId) => {
    try {
      const res = await fetch(`${API_URL}/dashboard/progress/${projectId}`);
      const d = await res.json();
      if (d && d.success) setProjectStats(prev => ({ ...prev, [projectId]: d }));
    } catch (error) {}
  };

  const loadStatusSummary = async () => {
    try {
      const res = await fetch(`${API_URL}/analytics/summary`);
      const d = await res.json();
      setStatusSummary(d);
    } catch (error) {}
  };

  const handleNavigation = (path) => navigate(path);
  const handleProjectClick = (projectId) => navigate(`/project/${projectId}`);

  // Archive (make inactive) a project straight from the dashboard opening page.
  const archiveProject = async (project, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm(`Archive "${project.name}"?\n\nIt moves to Inactive (hidden from the dashboard) — nothing is deleted. Reactivate any time from View all → Show Inactive.`)) return;
    try {
      const res = await fetch(`${API_URL}/projects/${project.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: false }),
      });
      if (!res.ok) throw new Error('Request failed');
      setProjects((prev) => prev.filter((p) => p.id !== project.id));
    } catch (err) {
      window.alert('Failed to archive: ' + (err.message || err));
    }
  };

  const handleDeleteProject = async (projectId, projectName, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${projectName}"? This cannot be undone.`)) return;
    try {
      await projectAPI.delete(projectId);
      const response = await projectAPI.getAll();
      const projectsData = response.data || response || [];
      setProjects(projectsData.map((project, index) => ({
        id: project.id, name: project.name,
        clientName: project.client_info?.full_name || 'Unknown Client',
        address: project.client_info?.address || '',
        cover_image: project.cover_image || '',
        color: PROJECT_COLORS[index % PROJECT_COLORS.length]
      })));
    } catch (error) {
      alert('Failed: ' + error.message);
    }
  };

  const handleSendEmail = async () => {
    if (sendingEmail) return; // Prevent double-click
    setSendingEmail(true);
    try {
      const BACKEND_URL = window.ENV?.REACT_APP_BACKEND_URL || window.location.origin;
      const response = await fetch(`${BACKEND_URL}/api/send-questionnaire`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_email: emailData.email,
          client_name: emailData.name,
          sender_name: "Established Design Co."
        })
      });
      if (response.ok) {
        alert('Email sent successfully!');
        setShowEmailModal(false);
        setEmailData({ email: '', name: '' });
      } else {
        const error = await response.json();
        alert('Failed: ' + (error.detail || 'Unknown error'));
      }
    } catch (error) {
      alert('Failed: ' + error.message);
    } finally {
      setSendingEmail(false);
    }
  };

  // Quick Actions = real, global app features
  const ACTIONS = [
    { label: 'New Home', icon: Home, onClick: () => setShowWizard(true) },
    { label: 'New Client', icon: UserPlus, onClick: () => handleNavigation('/customer') },
    { label: 'Questionnaire', icon: ClipboardList, onClick: () => handleNavigation('/customer/questionnaire') },
    { label: 'Email Client', icon: Mail, onClick: () => setShowEmailModal(true) },
    { label: 'Master To-Do', icon: ListTodo, onClick: () => handleNavigation('/master-todo') },
    { label: 'AI Studio', icon: Bot, onClick: () => handleNavigation('/ai-studio') },
    { label: 'Calculators', icon: Calculator, onClick: () => handleNavigation('/calculators') },
    { label: 'Contacts', icon: Users, onClick: () => handleNavigation('/master-contacts') },
    { label: 'Materials', icon: Package, onClick: () => handleNavigation('/master-materials') },
    { label: 'Backup', icon: Download, onClick: doBackup },
  ];

  // Status Overview donut, sourced from /analytics/summary (real counts)
  const statusEntries = statusSummary?.items_by_status
    ? Object.entries(statusSummary.items_by_status).map(([label, n]) => ({ label, n, toneKey: STATUS_TONE[label.toUpperCase()] || 'grey' }))
    : [];
  const statusTotal = statusEntries.reduce((s, x) => s + x.n, 0) || 0;
  const statusMax = statusEntries.reduce((m, x) => Math.max(m, x.n), 1);
  let acc = 0;
  const donutStops = statusTotal > 0
    ? statusEntries.map((s) => {
        const from = (acc / statusTotal) * 360; acc += s.n; const to = (acc / statusTotal) * 360;
        return `${tone(s.toneKey)} ${from}deg ${to}deg`;
      }).join(', ')
    : `${T.cardSubtle} 0deg 360deg`;

  const runCmd = (fn) => { setCmdOpen(false); setCmdQuery(''); fn(); };
  const cmdResults = (() => {
    const q = cmdQuery.trim().toLowerCase();
    const projHits = projects
      .filter((p) => !q || p.name.toLowerCase().includes(q) || (p.clientName || '').toLowerCase().includes(q))
      .map((p) => ({ label: p.name, sub: p.clientName, icon: '🏠', action: () => runCmd(() => navigate(`/project/${p.id}`)) }));
    const ffe = (pid) => runCmd(() => navigate(`/project/${pid}?tab=${encodeURIComponent('FF&E')}`));
    const itemHits = (cmdRemote.items || []).map((it) => ({
      label: it.name, sub: `${it.room_name || it.project_name}${it.vendor ? ' · ' + it.vendor : ''}`, icon: '🛋', action: () => ffe(it.project_id),
    }));
    const roomHits = (cmdRemote.rooms || []).map((r) => ({
      label: r.name, sub: `Room · ${r.project_name}`, icon: '🚪', action: () => ffe(r.project_id),
    }));
    const vendorHits = (cmdRemote.vendors || []).map((v) => ({
      label: v.vendor, sub: `Vendor · ${v.project_name}`, icon: '🏷', action: () => ffe(v.project_id),
    }));
    const secHits = CMD_SECTIONS
      .filter((s) => !q || s.label.toLowerCase().includes(q))
      .map((s) => ({ label: s.label, sub: 'Page', icon: '↗', action: () => runCmd(() => navigate(s.path)) }));
    return [...projHits, ...itemHits, ...roomHits, ...vendorHits, ...secHits].slice(0, 12);
  })();

  const Card = ({ accent, children, className = '', style = {}, ...rest }) => (
    <div {...rest} className={`rounded-xl overflow-hidden ${className}`}
      style={{ background: T.card, borderWidth: '1px', borderStyle: 'solid', borderColor: T.border, borderTopWidth: accent ? '3px' : '1px', borderTopColor: accent ? tone(accent) : T.border, boxShadow: T.shadow, ...style }}>
      {children}
    </div>
  );

  return (
    <div data-testid="main-dashboard" className="min-h-screen -mx-4 -my-6 px-4 py-5 sm:px-6"
      style={{ background: T.page, color: T.text, fontFamily: "'Jost','Century Gothic',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif", transition: 'background .35s, color .35s' }}>
      {/* HEADER */}
      <div className="mx-auto flex max-w-[1700px] flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl" style={{ color: T.text }}>Dashboard</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button data-testid="new-client-btn" onClick={() => handleNavigation('/customer')} className="rounded-lg px-3 py-1.5 text-xs font-semibold transition-all" style={{ background: T.chip, color: T.text, border: `1px solid ${T.border}` }}>+ New Client</button>
          <button data-testid="new-home-wizard-btn" onClick={() => setShowWizard(true)} className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-all" style={{ background: T.goldGrad, color: T.onGold }}><Home size={13} /> New Home</button>
          <button data-testid="dash-quest-btn" onClick={() => handleNavigation('/customer/questionnaire')} className="rounded-lg px-3 py-1.5 text-xs font-semibold transition-all" style={{ background: T.chip, color: T.text, border: `1px solid ${T.border}` }}>📋 Quest</button>
          <button data-testid="dash-email-btn" onClick={() => setShowEmailModal(true)} className="rounded-lg px-3 py-1.5 text-xs font-semibold transition-all" style={{ background: T.chip, color: T.text, border: `1px solid ${T.border}` }}>📧 Email</button>
          <button data-testid="dash-backup-btn" onClick={doBackup} className="rounded-lg px-3 py-1.5 text-xs font-semibold transition-all" style={{ background: T.chip, color: T.text, border: `1px solid ${T.border}` }}>💾 Backup</button>
          <button data-testid="dashboard-search-btn" onClick={() => setCmdOpen(true)} title="Quick jump (⌘K)" className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all" style={{ background: T.chip, color: T.text2, border: `1px solid ${T.border}` }}>
            <Search size={13} /> Search <span className="rounded px-1 py-0.5 text-[9px]" style={{ background: T.cardSubtle, color: T.muted }}>⌘K</span>
          </button>
          <button data-testid="theme-toggle-button" onClick={toggle} title="Toggle light / dark" className="flex items-center gap-1 rounded-full p-1" style={{ background: T.chip, border: `1px solid ${T.border}` }}>
            <span className="grid h-6 w-6 place-items-center rounded-full" style={{ background: !dark ? T.goldGrad : 'transparent', color: !dark ? T.onGold : T.muted }}><Sun size={13} /></span>
            <span className="grid h-6 w-6 place-items-center rounded-full" style={{ background: dark ? T.goldGrad : 'transparent', color: dark ? T.onGold : T.muted }}><Moon size={13} /></span>
          </button>
        </div>
      </div>

      {/* GRID */}
      <div className="mx-auto mt-5 grid max-w-[1700px] grid-cols-1 gap-6 lg:grid-cols-12">
        {/* ACTIVE PROJECTS */}
        <section className="lg:col-span-8">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-lg font-semibold sm:text-xl" style={{ color: T.text }}>Active Projects <span style={{ color: T.muted }}>({projects.length})</span></h2>
            <button onClick={() => handleNavigation('/projects')} className="flex items-center gap-1 text-xs font-medium" style={{ color: T.gold }}>View all <ChevronRight size={13} /></button>
          </div>
          {loading ? (
            <div className="py-12 text-center text-sm" style={{ color: T.muted }}>Loading…</div>
          ) : projects.length === 0 ? (
            <div className="py-12 text-center text-sm" style={{ color: T.muted }}>No projects yet. <button onClick={() => setShowWizard(true)} className="underline" style={{ color: T.gold }}>Create one</button></div>
          ) : (
            <DragDropContext onDragEnd={onProjectsDragEnd}>
             <Droppable droppableId="dashboard-projects">
              {(dp) => (
               <div ref={dp.innerRef} {...dp.droppableProps} className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
                {projects.map((project, i) => {
                const stat = projectStats[project.id];
                const pct = stat ? Math.round(stat.completion_percentage || 0) : null;
                const items = stat ? stat.total_items : null;
                const toolsOpen = expandedTools === project.id;
                const coverSrc = project.cover_image
                  ? (project.cover_image.startsWith('http') ? project.cover_image : `${BACKEND_ROOT}${project.cover_image}`)
                  : COVERS[i % COVERS.length];
                return (
                  <Draggable draggableId={project.id} index={i} key={project.id}>
                   {(prov, snapshot) => (
                  <ProjectCard
                    innerRef={prov.innerRef}
                    draggableProps={prov.draggableProps}
                    dragHandleProps={prov.dragHandleProps}
                    isDragging={snapshot.isDragging}
                    project={project}
                    theme={theme}
                    items={items}
                    pct={pct}
                    onDelete={(p, e) => handleDeleteProject(p.id, p.name, e)}
                    onCoverUpload={uploadCover}
                    onCoverSet={setCoverUrl}
                    coverBusy={coverBusy === project.id}
                    onStatusChange={changeProjectStatus}
                    onCloseOut={(p) => archiveProject(p)}
                    actions={
                      <button data-testid={`toggle-tools-${project.id}`} onClick={() => setExpandedTools(toolsOpen ? null : project.id)} className="flex items-center gap-1 rounded px-2 py-1 text-[10px] font-semibold" style={{ background: toolsOpen ? T.goldGrad : T.chip, color: toolsOpen ? T.onGold : T.gold, border: `1px solid ${T.border}` }}>
                        ⬆ Import <ChevronDown size={11} className={toolsOpen ? 'rotate-180 transition-transform' : 'transition-transform'} />
                      </button>
                    }
                    expandedContent={toolsOpen && (
                      <div className="mt-2 flex flex-wrap gap-1.5 rounded-lg p-2" style={{ background: T.cardSubtle, border: `1px solid ${T.border}` }}>
                        <label data-testid={`houzz-import-ffe-${project.id}`} title="Import a Houzz Pro FF&E Schedule PDF into this project's FF&E" className="cursor-pointer rounded px-2 py-1 text-[10px] font-medium" style={{ background: `${T.gold}22`, color: T.gold }}>
                          {houzzBusy === `${project.id}:ffe` ? '⏳…' : '⬆ FF&E PDF'}
                          <input type="file" accept="application/pdf,.pdf" disabled={!!houzzBusy} onChange={(e) => previewHouzzImport(project, 'ffe', e)} className="hidden" />
                        </label>
                        <label data-testid={`houzz-import-checklist-${project.id}`} title="Import a Houzz Pro FF&E Schedule PDF into this project's CHECKLIST" className="cursor-pointer rounded px-2 py-1 text-[10px] font-medium" style={{ background: T.chip, color: T.text2, border: `1px solid ${T.border}` }}>
                          {houzzBusy === `${project.id}:checklist` ? '⏳…' : '⬆ Checklist PDF'}
                          <input type="file" accept="application/pdf,.pdf" disabled={!!houzzBusy} onChange={(e) => previewHouzzImport(project, 'checklist', e)} className="hidden" />
                        </label>
                        <label data-testid={`finish-import-${project.id}`} title="Match a Houzz tear-sheet PDF to items by SKU and attach each product's finish/color swatch" className="cursor-pointer rounded px-2 py-1 text-[10px] font-medium" style={{ background: `${T.success}22`, color: T.success }}>
                          {houzzBusy === `${project.id}:finish` ? '⏳…' : '🎨 Finish Photos'}
                          <input type="file" accept="application/pdf,.pdf" disabled={!!houzzBusy} onChange={(e) => handleFinishImport(project, e)} className="hidden" />
                        </label>
                        <label data-testid={`finish-upgrade-cover-${project.id}`} title="Also replace each product's cover with the tear sheet's hi-res main photo" className="flex cursor-pointer items-center gap-1 self-center text-[9px]" style={{ color: T.muted }}>
                          <input type="checkbox" checked={finishUpgradeCover} onChange={(e) => setFinishUpgradeCover(e.target.checked)} className="h-2.5 w-2.5" /> hi-res cover
                        </label>
                        <button onClick={(e) => openMissingFinish(project, e)} disabled={missingBusy} data-testid={`fix-finishes-${project.id}`} title="List every FF&E item still missing a finish swatch" className="rounded px-2 py-1 text-[10px] font-medium disabled:opacity-50" style={{ background: `${T.danger}22`, color: T.danger }}>
                          {missingBusy && !missingFinish ? '⏳…' : '🩹 Fix Finishes'}
                        </button>
                        <button onClick={(e) => openVendorOrderSheet(project, e)} data-testid={`vendor-order-sheet-${project.id}`} title="Open a printable purchasing sheet, grouped by vendor" className="rounded px-2 py-1 text-[10px] font-medium" style={{ background: `${T.purple}22`, color: T.purple }}>🧾 Vendor Order Sheet</button>
                        <button onClick={(e) => openPdfLibrary(project, e)} data-testid={`saved-pdfs-${project.id}`} title="Every FF&E schedule & tear-sheet PDF you've uploaded — re-run any of them without re-uploading" className="rounded px-2 py-1 text-[10px] font-medium" style={{ background: `${T.teal}22`, color: T.teal }}>📂 Saved PDFs ({(savedPdfs[project.id] || []).length})</button>
                        {lastImports[project.id] && (
                          <button onClick={(e) => undoLastImport(project, e)} disabled={houzzBusy === `${project.id}:undo`} data-testid={`undo-import-${project.id}`} title="Roll back the last import" className="rounded px-2 py-1 text-[10px] font-medium disabled:opacity-50" style={{ background: `${T.warning}22`, color: T.warning }}>
                            {houzzBusy === `${project.id}:undo` ? '⏳…' : `↩ Undo (${lastImports[project.id].created_items + lastImports[project.id].updated_items})`}
                          </button>
                        )}
                      </div>
                    )}
                  />
                   )}
                  </Draggable>
                );
                })}
                {dp.placeholder}
               </div>
              )}
             </Droppable>
            </DragDropContext>
          )}
        </section>

        {/* QUICK ACTIONS */}
        <section className="lg:col-span-4">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-lg font-semibold sm:text-xl" style={{ color: T.text }}>Quick Actions</h2>
          </div>
          <Card>
            <div className="grid grid-cols-3 gap-2 p-4">
              {ACTIONS.map((a) => (
                <button key={a.label} data-testid={`action-${a.label.toLowerCase().replace(/[^a-z]+/g, '-')}`} onClick={a.onClick}
                  className="flex aspect-square flex-col items-center justify-center gap-1.5 rounded-lg text-center transition-all"
                  style={{ background: T.cardSubtle, border: `1px solid ${T.border}` }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.04)'; e.currentTarget.style.borderColor = T.gold; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.borderColor = T.border; }}>
                  <a.icon size={18} style={{ color: T.gold }} />
                  <span className="px-1 text-[10px] font-medium leading-tight" style={{ color: T.text2 }}>{a.label}</span>
                </button>
              ))}
            </div>
          </Card>
        </section>

        {/* BIG CALENDAR */}
        <section className="lg:col-span-8">
          <Card accent="gold">
            <div className="p-3">
              <ProjectCalendar theme={theme} compact={false} onEventClick={(event) => { if (event.projectId) handleNavigation(`/project/${event.projectId}?tab=FF&E`); }} />
            </div>
          </Card>
        </section>

        {/* TO-DO — the ONE rich To-Do component, in company/master mode.
            Same look, tagging, linking, comments and inline status as inside a
            project; each task shows its project + the sheet it lives on. */}
        <section className="lg:col-span-4">
          <div className="overflow-y-auto" style={{ maxHeight: 900 }}>
            <ToDoList masterMode />
          </div>
        </section>

        {/* STATUS OVERVIEW */}
        <section className="lg:col-span-12">
          <Card accent="gold">
            <div className="grid grid-cols-1 gap-6 p-5 lg:grid-cols-12">
              <div className="lg:col-span-4">
                <h2 className="text-lg font-semibold sm:text-xl" style={{ color: T.text }}>Status Overview</h2>
                <div className="text-xs" style={{ color: T.text2 }}>{statusTotal} items · all projects</div>
                <div className="mt-4 flex items-center gap-4">
                  <div className="relative h-28 w-28 shrink-0 rounded-full" style={{ background: `conic-gradient(${donutStops})` }}>
                    <div className="absolute inset-[12px] grid place-items-center rounded-full" style={{ background: T.card }}>
                      <div className="text-center">
                        <div className="text-xl font-bold leading-none" style={{ color: T.text }}>{statusTotal}</div>
                        <div className="text-[9px]" style={{ color: T.muted }}>ITEMS</div>
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 space-y-1.5">
                    {statusEntries.slice(0, 5).map((s) => (
                      <div key={s.label} className="flex items-center gap-2 text-xs">
                        <span className="h-2.5 w-2.5 rounded-sm" style={{ background: tone(s.toneKey) }} />
                        <span className="flex-1 truncate" style={{ color: T.text2 }}>{s.label}</span>
                        <span className="font-semibold" style={{ color: T.text }}>{s.n}</span>
                      </div>
                    ))}
                    {statusEntries.length === 0 && <div className="text-xs" style={{ color: T.muted }}>No items yet.</div>}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-x-8 gap-y-2.5 sm:grid-cols-2 lg:col-span-8">
                {statusEntries.map((s) => (
                  <div key={s.label}>
                    <div className="mb-0.5 flex items-center justify-between text-[12px]">
                      <span style={{ color: T.text2 }}>{s.label}</span><span className="font-semibold" style={{ color: T.text }}>{s.n}</span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full" style={{ background: T.cardSubtle }}>
                      <div className="h-full rounded-full" style={{ width: `${(s.n / statusMax) * 100}%`, background: tone(s.toneKey) }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </section>
      </div>

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="rounded-lg p-6 w-full max-w-md" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #0f0f1a 100%)', border: '1px solid #8b7355' }}>
            <h3 className="text-xl text-[#D4C5A9] mb-4">Send Questionnaire Email</h3>
            <div className="space-y-4">
              <div><label className="block text-stone-400 text-sm mb-1">Client Name</label><input type="text" value={emailData.name} onChange={(e) => setEmailData({...emailData, name: e.target.value})} className="w-full bg-black/50 border border-[#8b7355] rounded px-3 py-2 text-white" placeholder="Enter client name" /></div>
              <div><label className="block text-stone-400 text-sm mb-1">Client Email</label><input type="email" value={emailData.email} onChange={(e) => setEmailData({...emailData, email: e.target.value})} className="w-full bg-black/50 border border-[#8b7355] rounded px-3 py-2 text-white" placeholder="Enter client email" /></div>
              <div className="flex justify-end gap-3 mt-6">
                <button onClick={() => setShowEmailModal(false)} className="px-4 py-2 rounded text-stone-400 hover:text-white">Cancel</button>
                <button onClick={handleSendEmail} disabled={sendingEmail} className="px-4 py-2 rounded text-white disabled:opacity-50" style={{ background: 'linear-gradient(135deg, #8b7355 0%, #a0845c 100%)' }}>{sendingEmail ? 'Sending...' : 'Send'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* New-Home Wizard */}
      {showWizard && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" data-testid="new-home-wizard-modal">
          <div className="rounded-lg p-6 w-full max-w-md" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #0f0f1a 100%)', border: '1px solid #8b7355' }}>
            <h3 className="text-xl text-[#D4C5A9] mb-1">🏠 Spin up a new home</h3>
            <p className="text-stone-400 text-xs mb-4">Creates the project and auto-builds every trade sheet from your default templates.</p>
            <div className="space-y-4">
              <div><label className="block text-stone-400 text-sm mb-1">Home / Project Name</label>
                <input data-testid="wizard-name-input" type="text" value={wizName} onChange={(e) => setWizName(e.target.value)} className="w-full bg-black/50 border border-[#8b7355] rounded px-3 py-2 text-white" placeholder="e.g. Wheeler Ridge Residence" /></div>
              <div><label className="block text-stone-400 text-sm mb-1">Client Name (optional)</label>
                <input data-testid="wizard-client-input" type="text" value={wizClient} onChange={(e) => setWizClient(e.target.value)} className="w-full bg-black/50 border border-[#8b7355] rounded px-3 py-2 text-white" placeholder="e.g. The Wheelers" /></div>
              <div className="flex justify-end gap-3 mt-6">
                <button data-testid="wizard-cancel" onClick={() => setShowWizard(false)} className="px-4 py-2 rounded text-stone-400 hover:text-white">Cancel</button>
                <button data-testid="wizard-create" onClick={runNewHomeWizard} disabled={wizBusy || !wizName.trim()} className="px-4 py-2 rounded text-black font-bold disabled:opacity-50" style={{ background: 'linear-gradient(135deg,#D4A574,#b8863f)' }}>{wizBusy ? 'Building…' : '⚡ Create & auto-build'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {houzzPreview && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="houzz-preview-modal">
          <div className="rounded-lg w-full max-w-2xl max-h-[85vh] flex flex-col" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #0f0f1a 100%)', border: '1px solid #8b7355' }}>
            <div className="p-5 border-b border-[#8b7355]/40">
              <h3 className="text-xl text-[#D4C5A9] mb-1">📋 Import preview — {houzzPreview.project.name}</h3>
              <p className="text-stone-400 text-xs">
                Target: <span className="text-[#e2c08a] font-semibold">{houzzPreview.target === 'checklist' ? 'CHECKLIST only (transfer to FF&E manually later)' : 'FF&E + Checklist (both)'}</span>
                {' · '}<span className="text-emerald-300">{houzzPreview.data.will_add} new</span>
                {' · '}<span className="text-amber-300">{houzzPreview.data.will_update} refreshed</span>
                {' · '}{houzzPreview.data.rooms_named.length} rooms
              </p>
            </div>
            <div className="flex-1 overflow-auto p-3">
              <div className="text-[11px] text-stone-300">
                <div className="flex items-center gap-2 px-2 py-1 text-stone-500 sticky top-0 bg-[#141422] font-semibold">
                  <div className="w-14 flex-shrink-0"></div>
                  <div className="flex-1 min-w-0">Item</div>
                  <div className="w-20 flex-shrink-0">Room</div>
                  <div className="w-20 flex-shrink-0">Vendor</div>
                  <div className="w-16 flex-shrink-0">SKU</div>
                  <div className="w-16 flex-shrink-0 text-right">Price</div>
                </div>
                {(houzzPreview.data.items || []).map((it, i) => (
                  <div key={i} className="flex items-center gap-2 px-2 py-1.5 border-t border-white/5" data-testid={`preview-row-${i}`}>
                    <div className="w-14 flex-shrink-0">
                      <span className={`px-1.5 py-0.5 rounded text-[9px] ${it.existing ? 'bg-amber-600/30 text-amber-200' : 'bg-emerald-600/30 text-emerald-200'}`}>{it.existing ? 'REFRESH' : 'NEW'}</span>
                    </div>
                    <div className="flex-1 min-w-0 flex items-center gap-2" title={it.name}>
                      {it.thumb ? (
                        <img src={it.thumb} alt="" data-testid={`preview-thumb-${i}`} className="w-9 h-9 object-cover rounded border border-white/10 flex-shrink-0" />
                      ) : (
                        <div data-testid={`preview-thumb-${i}`} className="w-9 h-9 rounded bg-white/5 flex items-center justify-center text-stone-600 text-[9px] flex-shrink-0">—</div>
                      )}
                      <span className="truncate">{it.name}</span>
                    </div>
                    <div className="w-20 flex-shrink-0 text-stone-400 truncate" title={it.room}>{it.room}</div>
                    <div className="w-20 flex-shrink-0 text-stone-400 truncate" title={it.vendor}>{it.vendor}</div>
                    <div className="w-16 flex-shrink-0 text-stone-500 truncate" title={it.sku}>{it.sku}</div>
                    <div className="w-16 flex-shrink-0 text-right">{it.price ? `$${it.price.toLocaleString()}` : ''}</div>
                  </div>
                ))}
              </div>
            </div>
            {houzzBusy === 'commit' && houzzProgress && (
              <div className="px-4 pb-1 pt-2" data-testid="houzz-preview-progress">
                <div className="flex items-center justify-between text-[11px] text-stone-300 mb-1">
                  <span className="truncate pr-2">{houzzProgress.stage || 'Working…'}</span>
                  <span className="tabular-nums text-[#D4A574] font-semibold">{Math.round(houzzProgress.pct || 0)}%</span>
                </div>
                <div className="h-2 w-full rounded-full bg-white/10 overflow-hidden">
                  <div data-testid="houzz-preview-progress-bar" className="h-full rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${Math.max(3, Math.min(100, houzzProgress.pct || 0))}%`, background: 'linear-gradient(90deg,#D4A574,#b8863f)' }} />
                </div>
                <div className="text-[10px] text-stone-500 mt-1">Big schedules can take a minute or two — you can leave this open.</div>
              </div>
            )}
            <div className="p-4 border-t border-[#8b7355]/40 flex justify-end gap-3">
              <button data-testid="houzz-preview-cancel" onClick={() => setHouzzPreview(null)} disabled={houzzBusy === 'commit'} className="px-4 py-2 rounded text-stone-400 hover:text-white disabled:opacity-50">Cancel</button>
              <button data-testid="houzz-preview-confirm" onClick={confirmHouzzImport} disabled={houzzBusy === 'commit'} className="px-4 py-2 rounded text-black font-bold disabled:opacity-50" style={{ background: 'linear-gradient(135deg,#D4A574,#b8863f)' }}>
                {houzzBusy === 'commit' ? 'Importing…' : `✓ Import ${houzzPreview.data.total} items`}
              </button>
            </div>
          </div>
        </div>
      )}

      {missingFinish && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="missing-finish-modal">
          <div className="rounded-lg w-full max-w-2xl max-h-[85vh] flex flex-col" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #0f0f1a 100%)', border: '1px solid #8b7355' }}>
            <div className="p-5 border-b border-[#8b7355]/40">
              <h3 className="text-xl text-[#D4C5A9] mb-1">🩹 Fix missing finishes — {missingFinish.project.name}</h3>
              <p className="text-stone-400 text-xs">
                {(missingFinish.items || []).length === 0
                  ? 'Every FF&E item already has a finish swatch. 🎉'
                  : <><span className="text-rose-300 font-semibold">{missingFinish.items.length}</span> FF&E item{missingFinish.items.length === 1 ? '' : 's'} still missing a finish swatch. Upload a flat material/color swatch for each (never the furniture photo).</>}
              </p>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <label onClick={(e) => e.stopPropagation()} data-testid="auto-match-finishes" title="Upload a Houzz tear-sheet PDF — we auto-match swatches to these items by SKU" className={`text-[11px] px-3 py-1.5 rounded cursor-pointer inline-flex items-center gap-1 font-semibold ${missingBusy ? 'bg-indigo-700/30 text-stone-400' : 'bg-indigo-600/60 text-indigo-50 hover:bg-indigo-600/80'}`}>
                  {missingBusy ? '⏳ Matching…' : '🎨 Auto-match from tear sheet'}
                  <input type="file" accept="application/pdf,.pdf" data-testid="auto-match-finishes-input" disabled={missingBusy} onChange={autoMatchFinishes} className="hidden" />
                </label>
                <span className="text-[10px] text-stone-500">Runs the SKU swatch-matcher, then leaves only the items you must pick by hand.</span>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-3">
              {(missingFinish.items || []).length === 0 ? (
                <div className="text-center text-stone-500 py-10 text-sm" data-testid="missing-finish-empty">All set — nothing to fix.</div>
              ) : (
                <div className="text-[11px] text-stone-300">
                  <div className="flex items-center gap-2 px-2 py-1 text-stone-500 sticky top-0 bg-[#141422] font-semibold">
                    <div className="flex-1 min-w-0">Item</div>
                    <div className="w-20 flex-shrink-0">Room</div>
                    <div className="w-16 flex-shrink-0">SKU</div>
                    <div className="w-20 flex-shrink-0">Vendor</div>
                    <div className="w-24 flex-shrink-0 text-right">Finish</div>
                  </div>
                  {(missingFinish.items || []).map((it, i) => (
                    <div key={it.id} className="flex items-center gap-2 px-2 py-1.5 border-t border-white/5" data-testid={`missing-finish-row-${i}`}>
                      <div className="flex-1 min-w-0 flex items-center gap-2" title={it.name}>
                        {it.image_url ? (
                          <img src={it.image_url} alt="" className="w-9 h-9 object-cover rounded border border-white/10 flex-shrink-0" />
                        ) : (
                          <div className="w-9 h-9 rounded bg-white/5 flex items-center justify-center text-stone-600 text-[9px] flex-shrink-0">—</div>
                        )}
                        <span className="truncate">{it.name}</span>
                      </div>
                      <div className="w-20 flex-shrink-0 text-stone-400 truncate" title={it.room}>{it.room}</div>
                      <div className="w-16 flex-shrink-0 text-stone-500 truncate" title={it.sku}>{it.sku}</div>
                      <div className="w-20 flex-shrink-0 text-stone-400 truncate" title={it.vendor}>{it.vendor}</div>
                      <div className="w-24 flex-shrink-0 text-right">
                        <label onClick={(e) => e.stopPropagation()} data-testid={`missing-finish-upload-${it.id}`} className={`text-[10px] px-2 py-1 rounded cursor-pointer inline-block whitespace-nowrap ${missingBusy ? 'bg-emerald-700/30 text-stone-400' : 'bg-emerald-700/40 text-emerald-100 hover:bg-emerald-700/60'}`}>
                          ＋ Upload
                          <input type="file" accept="image/*" data-testid={`missing-finish-input-${it.id}`} disabled={missingBusy} onChange={(e) => uploadFinishForItem(it.id, e)} className="hidden" />
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-[#8b7355]/40 flex justify-end gap-3">
              <button data-testid="missing-finish-close" onClick={() => setMissingFinish(null)} className="px-4 py-2 rounded text-black font-bold" style={{ background: 'linear-gradient(135deg,#D4A574,#b8863f)' }}>Done</button>
            </div>
          </div>
        </div>
      )}
      {pdfLibrary && (
        <div className="fixed inset-0 z-[100000] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }} data-testid="pdf-library-modal">
          <div className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl" style={{ background: T.card, border: `1px solid ${T.borderStrong}`, boxShadow: T.shadowHover }}>
            <div className="p-5" style={{ borderBottom: `1px solid ${T.border}` }}>
              <h3 className="text-lg font-semibold" style={{ color: T.text }}>📂 Saved PDFs — {pdfLibrary.name}</h3>
              <p className="mt-1 text-xs" style={{ color: T.text2 }}>
                Every FF&amp;E schedule and finish tear-sheet you've uploaded is kept here. Re-run any of them against this project's current items — no re-uploading.
              </p>
            </div>
            <div className="flex-1 overflow-auto p-3">
              {(savedPdfs[pdfLibrary.id] || []).length === 0 ? (
                <div className="py-10 text-center text-sm" data-testid="pdf-library-empty" style={{ color: T.muted }}>
                  No saved PDFs yet. Import an FF&amp;E schedule or finish tear-sheet and it will be kept here automatically.
                </div>
              ) : (
                <div className="space-y-2">
                  {(savedPdfs[pdfLibrary.id] || []).map((doc) => {
                    const kindLabel = doc.kind === 'tear_sheet' ? 'Tear Sheet' : (doc.kind === 'checklist' ? 'Checklist PDF' : 'FF&E PDF');
                    const kindTone = doc.kind === 'tear_sheet' ? T.success : (doc.kind === 'checklist' ? T.brown : T.gold);
                    return (
                      <div key={doc.id} data-testid={`saved-pdf-row-${doc.id}`} className="flex items-center gap-3 rounded-lg p-3" style={{ background: T.cardSubtle, border: `1px solid ${T.border}` }}>
                        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-lg" style={{ background: `${kindTone}22`, color: kindTone }}>📄</div>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-[13px] font-medium" style={{ color: T.text }} title={doc.filename}>{doc.filename}</div>
                          <div className="text-[11px]" style={{ color: T.muted }}>
                            <span className="font-semibold" style={{ color: kindTone }}>{kindLabel}</span>
                            {doc.size ? ` · ${(doc.size / 1024).toFixed(0)} KB` : ''}
                            {doc.uploaded_at ? ` · ${new Date(doc.uploaded_at).toLocaleDateString()}` : ''}
                          </div>
                        </div>
                        <a href={`${API_URL}/projects/${pdfLibrary.id}/imported-pdfs/${doc.id}/download`} target="_blank" rel="noopener noreferrer" data-testid={`saved-pdf-download-${doc.id}`} className="rounded px-2.5 py-1.5 text-[11px] font-medium no-underline" style={{ background: T.chip, color: T.text2, border: `1px solid ${T.border}` }}>View</a>
                        <button onClick={() => rerunSavedPdf(pdfLibrary, doc)} disabled={rerunBusy || pdfBusy} data-testid={`saved-pdf-rerun-${doc.id}`} className="rounded px-2.5 py-1.5 text-[11px] font-semibold disabled:opacity-50" style={{ background: T.goldGrad, color: T.onGold }}>{rerunBusy ? '…' : '↻ Re-run'}</button>
                        <button onClick={() => deleteSavedPdf(pdfLibrary, doc)} disabled={pdfBusy} data-testid={`saved-pdf-delete-${doc.id}`} title="Remove from library" className="rounded px-2 py-1.5 text-[11px] font-medium disabled:opacity-50" style={{ background: `${T.danger}22`, color: T.danger }}>×</button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 p-4" style={{ borderTop: `1px solid ${T.border}` }}>
              <button data-testid="pdf-library-close" onClick={() => setPdfLibrary(null)} className="rounded px-4 py-2 text-sm font-bold" style={{ background: T.goldGrad, color: T.onGold }}>Done</button>
            </div>
          </div>
        </div>
      )}
      {rerunPreview && (
        <div className="fixed inset-0 z-[100001] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }} data-testid="rerun-preview-modal">
          <div className="w-full max-w-md overflow-hidden rounded-2xl" style={{ background: T.card, border: `1px solid ${T.borderStrong}`, boxShadow: T.shadowHover }}>
            <div className="p-5" style={{ borderBottom: `1px solid ${T.border}` }}>
              <h3 className="text-lg font-semibold" style={{ color: T.text }}>↻ Re-run "{rerunPreview.doc.filename}"?</h3>
              <p className="mt-1 text-xs" style={{ color: T.text2 }}>Here's what this will do to <span style={{ color: T.gold }}>{rerunPreview.project.name}</span> — nothing has changed yet.</p>
            </div>
            <div className="p-5">
              {rerunPreview.data.kind === 'tear_sheet' ? (
                <div className="space-y-2 text-sm" style={{ color: T.text }}>
                  <div className="flex justify-between"><span style={{ color: T.text2 }}>Products in tear sheet</span><span className="font-semibold">{rerunPreview.data.products}</span></div>
                  <div className="flex justify-between"><span style={{ color: T.text2 }}>With a real swatch</span><span className="font-semibold">{rerunPreview.data.with_swatch}</span></div>
                  <div className="flex justify-between"><span style={{ color: T.text2 }}>Match items in this project</span><span className="font-semibold">{rerunPreview.data.matchable}</span></div>
                  <div className="flex justify-between rounded-lg px-3 py-2" style={{ background: `${T.success}18` }}><span className="font-semibold" style={{ color: T.success }}>Will update finishes on</span><span className="font-bold" style={{ color: T.success }}>{rerunPreview.data.will_update} items</span></div>
                </div>
              ) : (
                <div className="space-y-2 text-sm" style={{ color: T.text }}>
                  <div className="flex justify-between"><span style={{ color: T.text2 }}>Target</span><span className="font-semibold">{rerunPreview.data.kind === 'checklist' ? 'Checklist' : 'FF&E + Checklist'}</span></div>
                  <div className="flex justify-between rounded-lg px-3 py-2" style={{ background: `${T.success}18` }}><span className="font-semibold" style={{ color: T.success }}>New items to add</span><span className="font-bold" style={{ color: T.success }}>{rerunPreview.data.will_add}</span></div>
                  <div className="flex justify-between rounded-lg px-3 py-2" style={{ background: `${T.warning}18` }}><span className="font-semibold" style={{ color: T.warning }}>Existing items to refresh</span><span className="font-bold" style={{ color: T.warning }}>{rerunPreview.data.will_update}</span></div>
                  <div className="flex justify-between"><span style={{ color: T.text2 }}>Total in PDF</span><span className="font-semibold">{rerunPreview.data.total}</span></div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 p-4" style={{ borderTop: `1px solid ${T.border}` }}>
              <button data-testid="rerun-cancel" onClick={() => setRerunPreview(null)} className="rounded px-4 py-2 text-sm font-medium" style={{ color: T.text2 }}>Cancel</button>
              <button data-testid="rerun-confirm" onClick={confirmRerun} disabled={rerunBusy} className="rounded px-4 py-2 text-sm font-bold disabled:opacity-50" style={{ background: T.goldGrad, color: T.onGold }}>{rerunBusy ? 'Running…' : 'Confirm re-run'}</button>
            </div>
          </div>
        </div>
      )}

      {cmdOpen && (
        <div className="fixed inset-0 z-[100002] flex items-start justify-center pt-28" style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(6px)' }} onClick={() => setCmdOpen(false)} data-testid="cmdk-overlay">
          <div className="w-[460px] max-w-[92vw] overflow-hidden rounded-2xl" style={{ background: T.card, border: `1px solid ${T.borderStrong}`, boxShadow: T.shadowHover }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: `1px solid ${T.border}` }}>
              <Search size={15} style={{ color: T.gold }} />
              <input autoFocus value={cmdQuery} onChange={(e) => setCmdQuery(e.target.value)} placeholder="Jump to a project, FF&E item, room or vendor…" data-testid="cmdk-input" className="flex-1 bg-transparent text-sm outline-none" style={{ color: T.text }} />
              <span className="rounded px-1.5 py-0.5 text-[10px]" style={{ background: T.cardSubtle, color: T.muted }}>ESC</span>
            </div>
            <div className="max-h-72 overflow-y-auto py-1">
              {cmdResults.length === 0 ? (
                <div className="px-4 py-6 text-center text-xs" style={{ color: T.muted }}>No matches</div>
              ) : cmdResults.map((r, i) => (
                <div key={i} data-testid={`cmdk-result-${i}`} onClick={r.action} className="flex cursor-pointer items-center gap-3 px-4 py-2.5 text-sm" style={{ color: T.text }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = T.cardSubtle; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                  <span className="grid h-6 w-6 place-items-center rounded" style={{ background: `${T.gold}22`, color: T.gold }}>{r.icon}</span>
                  <span className="flex-1 truncate">{r.label}</span>
                  <span className="text-[10px]" style={{ color: T.muted }}>{r.sub}</span>
                  <CornerDownLeft size={12} style={{ color: T.muted }} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MainDashboard;
